import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { writePrintDoc } from '../utils/printUtils';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Users, Edit, Trash2, Eye, Camera, Upload, Printer } from 'lucide-react';
import Webcam from 'react-webcam';
import { useAuth } from '../AuthContext';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';
import PaginationControls from '../components/PaginationControls';
import useAutoRefresh from '../hooks/useAutoRefresh';
import { getStates, getDistricts } from '../data/indianStatesDistricts';

// Print helper — opens a new window with a clean printable staff list
const printStaffList = (staff, branches, selectedBranchFilter, filterType) => {
  const branchName = selectedBranchFilter === 'all'
    ? 'All Branches'
    : (branches.find(b => b.id === selectedBranchFilter)?.name || '-');
  const typeLabel = filterType === 'all' ? 'All Staff' : filterType;
  const rows = staff.map((m, i) => {
    const br = branches.find(b => b.id === m.branch_id);
    return `<tr style="background:${i % 2 ? '#f9fafb' : '#fff'}">
      <td>${i + 1}</td>
      <td>${m.name || '-'}</td>
      <td>${m.staff_type || '-'}</td>
      <td>${m.designation || '-'}</td>
      <td>${br?.name || '-'}</td>
      <td>${m.mobile || '-'}</td>
      <td>${m.gender || '-'}</td>
      <td>${m.status || '-'}</td>
    </tr>`;
  }).join('');
  const html = `<!doctype html><html><head><title>Staff — WWAGS MIS</title>
  <style>
    body{font-family:Arial,sans-serif;padding:24px;color:#0f172a}
    h1{color:#047857;font-size:20px;margin:0 0 4px}
    .meta{color:#64748b;font-size:12px;margin-bottom:14px}
    table{width:100%;border-collapse:collapse;font-size:11px}
    th{background:#047857;color:#fff;padding:8px;text-align:left}
    td{padding:6px 8px;border-bottom:1px solid #e2e8f0}
    @media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
  </style></head><body>
    <h1>WWAGS MIS — Staff List</h1>
    <p class="meta">Branch: <b>${branchName}</b> &nbsp;•&nbsp; Type: <b>${typeLabel}</b> &nbsp;•&nbsp; Total: <b>${staff.length}</b> &nbsp;•&nbsp; Generated: ${new Date().toLocaleString('en-IN')}</p>
    <table>
      <thead><tr><th>#</th><th>Name</th><th>Type</th><th>Designation</th><th>Branch</th><th>Mobile</th><th>Gender</th><th>Status</th></tr></thead>
      <tbody>${rows || '<tr><td colspan="8" style="text-align:center;color:#94a3b8;padding:20px">No staff</td></tr>'}</tbody>
    </table>
  </body></html>`;
  const w = window.open('', '_blank');
  if (w) { writePrintDoc(w.document, html); setTimeout(() => w.print(), 500); }
};

// Avatar component to handle photo display with error fallback
const StaffAvatar = ({ member }) => {
  const [imgError, setImgError] = useState(false);
  const initials = member.name.split(' ').map(n => n.charAt(0).toUpperCase()).slice(0, 2).join('');
  
  if (!member.photo_url || imgError) {
    return (
      <div className="w-10 h-12 rounded bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-lg border border-blue-200">
        {initials}
      </div>
    );
  }
  
  const photoSrc = member.photo_url.startsWith('data:') 
    ? member.photo_url 
    : `${process.env.REACT_APP_BACKEND_URL}${member.photo_url}`;
  
  return (
    <img
      src={photoSrc}
      alt={member.name}
      className="w-10 h-12 rounded object-cover border border-slate-200"
      onError={() => setImgError(true)}
    />
  );
};

const Staff = () => {
  const { hasPermission, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [staff, setStaff] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [selectedBranchFilter, setSelectedBranchFilter] = useState('all');
  
  // Photo capture states
  const [showWebcam, setShowWebcam] = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const webcamRef = useRef(null);
  const [showCustomPosition, setShowCustomPosition] = useState(false);
  const [customPosition, setCustomPosition] = useState('');

  const STAFF_POSITIONS = [
    // BRC MC Positions
    'BRC MC - Chairman',
    'BRC MC - Vice-Chairman',
    'BRC MC - President',
    'BRC MC - Secretary',
    'BRC MC - Joint Secretary',
    'BRC MC - Treasurer',
    'BRC MC - Members',
    // Staff Positions
    'Block Project Manager',
    'Care Taker',
    'Director',
    'Mentor',
    'Program Coordinator',
    'Project Manager',
    'Asst. Mentor',
    'MTS',
    'Field Officer',
    'CRP-EP',
    'Accountant',
    'State Coordinator',
    'Other'
  ];

  const [formData, setFormData] = useState({
    name: '',
    staff_type: '',
    date_of_joining: '',
    date_of_birth: '',
    social_caste: '',
    job_type: '',
    address: '',
    district: '',
    state: '',
    mobile: '',
    gender: 'Female',
    branch_id: '',
    id_type: '',
    id_number: '',
  });

  // Edit mode state
  const [editMode, setEditMode] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    fetchData();
  }, [filterType, selectedBranchFilter, page, pageSize]);

  // Real-time auto-refresh (lighter interval — 2 minutes — since list can be large)
  useAutoRefresh(() => fetchData(), { intervalMs: 120000 });

  const fetchData = async () => {
    try {
      const params = {
        skip: page * pageSize,
        limit: pageSize,
        include_photos: false,
      };
      if (filterType !== 'all') params.staff_type = filterType;
      if (selectedBranchFilter !== 'all') params.branch_id = selectedBranchFilter;
      const [staffRes, branchesRes] = await Promise.all([
        api.get('/staff', { params }),
        api.get('/branches'),
      ]);
      setStaff(staffRes.data);
      setTotalCount(parseInt(staffRes.headers['x-total-count'] || '0', 10) || staffRes.data.length);
      setBranches(branchesRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchStaff = async () => {
    try {
      const response = await api.get('/staff', {
        params: {
          skip: page * pageSize,
          limit: pageSize,
          include_photos: false,
          staff_type: filterType !== 'all' ? filterType : undefined,
        },
      });
      setStaff(response.data);
      setTotalCount(parseInt(response.headers['x-total-count'] || '0', 10) || response.data.length);
    } catch (error) {
      toast.error('Failed to fetch staff');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('data', JSON.stringify(formData));
      
      // Add photo if captured
      if (capturedPhoto) {
        // Only re-upload if it's a new photo (not the existing one from DB)
        const isExistingPhoto = editMode && editingStaff?.photo_url && capturedPhoto === (editingStaff.photo_url.startsWith('data:') ? editingStaff.photo_url : `${process.env.REACT_APP_BACKEND_URL}${editingStaff.photo_url}`);
        if (!isExistingPhoto) {
          formDataToSend.append('photo', capturedPhoto);
        }
      }

      if (editMode && editingStaff) {
        await api.put(`/staff/${editingStaff.id}`, formDataToSend);
        toast.success('Staff member updated successfully');
      } else {
        await api.post('/staff', formDataToSend);
        toast.success('Staff member added successfully');
      }

      closeDialog();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || (editMode ? 'Failed to update staff' : 'Failed to add staff'));
    }
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditMode(false);
    setEditingStaff(null);
    setShowCustomPosition(false);
    setCustomPosition('');
    setCapturedPhoto(null);
    setFormData({
      name: '',
      staff_type: '',
      date_of_joining: '',
      date_of_birth: '',
      social_caste: '',
      job_type: '',
      address: '',
      district: '',
      state: '',
      mobile: '',
      gender: 'Female',
      branch_id: '',
      id_type: '',
      id_number: '',
    });
  };

  const handleEdit = async (member) => {
    setEditMode(true);
    // List omits photo for performance — fetch full record so photo shows in the edit form
    let full = member;
    try {
      const r = await api.get(`/staff/${member.id}`);
      full = r.data || member;
    } catch { /* fall back to list row */ }
    setEditingStaff(full);
    setFormData({
      name: full.name || '',
      staff_type: full.staff_type || '',
      date_of_joining: full.date_of_joining || '',
      date_of_birth: full.date_of_birth || '',
      social_caste: full.social_caste || '',
      job_type: full.job_type || '',
      address: full.address || '',
      district: full.district || '',
      state: full.state || '',
      mobile: full.mobile || '',
      gender: full.gender || 'Female',
      branch_id: full.branch_id || '',
      id_type: full.id_type || '',
      id_number: full.id_number || '',
      status: full.status || 'active',
    });
    // Check if staff_type is a custom position not in the list
    if (!STAFF_POSITIONS.includes(full.staff_type)) {
      setShowCustomPosition(true);
      setCustomPosition(full.staff_type || '');
    }
    // Set existing photo
    if (full.photo_url) {
      setCapturedPhoto(full.photo_url.startsWith('data:') ? full.photo_url : `${process.env.REACT_APP_BACKEND_URL}${full.photo_url}`);
    }
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!isAdmin()) {
      toast.error('Only Admin can delete staff');
      return;
    }
    try {
      await api.delete(`/staff/${id}`);
      toast.success('Staff deleted successfully');
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete staff');
    }
  };

  const openDeleteDialog = (member) => {
    setItemToDelete(member);
    setDeleteDialogOpen(true);
  };

  // Photo capture functions
  const capturePhoto = () => {
    const imageSrc = webcamRef.current?.getScreenshot();
    if (imageSrc) {
      setCapturedPhoto(imageSrc);
      setShowWebcam(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCapturedPhoto(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="p-6 md:p-8 lg:p-10" data-testid="staff-page">
      <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Staff Management</h1>
          <p className="text-lg text-slate-600">Manage BRC-MC, CER-EP & MTS staff</p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) closeDialog(); else setDialogOpen(true); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-staff-button" className="bg-emerald-700 hover:bg-emerald-800 text-white"
              onClick={() => { setEditMode(false); setEditingStaff(null); }}
            >
              <Plus className="w-4 h-4 mr-2" />
                Add Staff
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editMode ? 'Edit Staff Member' : 'Add New Staff Member'}</DialogTitle>
              </DialogHeader>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Passport Photo Section */}
                <div className="space-y-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <Label className="text-base font-semibold">Passport Photo</Label>
                  
                  <div className="flex flex-col items-center gap-4">
                    {showWebcam ? (
                      <div className="space-y-3">
                        <Webcam
                          ref={webcamRef}
                          audio={false}
                          screenshotFormat="image/jpeg"
                          className="rounded-lg border border-slate-300"
                          width={280}
                          height={210}
                        />
                        <div className="flex gap-2 justify-center">
                          <Button 
                            type="button" 
                            onClick={capturePhoto} 
                            className="bg-emerald-700 hover:bg-emerald-800"
                            data-testid="capture-staff-photo-button"
                          >
                            <Camera className="w-4 h-4 mr-2" />
                            Capture
                          </Button>
                          <Button type="button" variant="outline" onClick={() => setShowWebcam(false)}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : capturedPhoto ? (
                      <div className="space-y-3">
                        <img 
                          src={capturedPhoto} 
                          alt="Staff photo" 
                          className="w-32 h-32 rounded-lg object-cover border-2 border-emerald-300"
                        />
                        <Button 
                          type="button" 
                          variant="outline" 
                          size="sm" 
                          onClick={() => setCapturedPhoto(null)}
                        >
                          Remove Photo
                        </Button>
                      </div>
                    ) : (
                      <div className="flex gap-3">
                        <Button 
                          type="button" 
                          variant="outline" 
                          onClick={() => setShowWebcam(true)}
                          data-testid="open-staff-camera-button"
                        >
                          <Camera className="w-4 h-4 mr-2" />
                          Take Photo
                        </Button>
                        <div>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleFileUpload}
                            className="hidden"
                            id="staff-photo-upload"
                          />
                          <Button 
                            type="button" 
                            variant="outline" 
                            asChild
                          >
                            <label htmlFor="staff-photo-upload" className="cursor-pointer" data-testid="upload-staff-photo-button">
                              <Upload className="w-4 h-4 mr-2" />
                              Upload Photo
                            </label>
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      data-testid="staff-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="staff_type">Staff Type / Position *</Label>
                    <Select
                      value={showCustomPosition ? 'Other' : formData.staff_type}
                      onValueChange={(value) => {
                        if (value === 'Other') {
                          setShowCustomPosition(true);
                          setFormData({ ...formData, staff_type: '' });
                        } else {
                          setShowCustomPosition(false);
                          setCustomPosition('');
                          setFormData({ ...formData, staff_type: value });
                        }
                      }}
                    >
                      <SelectTrigger data-testid="staff-type-select">
                        <SelectValue placeholder="Select position" />
                      </SelectTrigger>
                      <SelectContent>
                        {STAFF_POSITIONS.map((pos) => (
                          <SelectItem key={pos} value={pos}>{pos}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {showCustomPosition && (
                      <Input
                        placeholder="Enter custom position"
                        value={customPosition}
                        onChange={(e) => {
                          setCustomPosition(e.target.value);
                          setFormData({ ...formData, staff_type: e.target.value });
                        }}
                        className="mt-2"
                        data-testid="custom-position-input"
                      />
                    )}
                  </div>
                </div>

                {/* Branch Selection - visible to Admin */}
                {isAdmin() && (
                  <div className="space-y-2">
                    <Label htmlFor="branch">Working Branch *</Label>
                    <Select
                      value={formData.branch_id}
                      onValueChange={(value) => setFormData({ ...formData, branch_id: value })}
                    >
                      <SelectTrigger data-testid="staff-branch-select">
                        <SelectValue placeholder="Select branch" />
                      </SelectTrigger>
                      <SelectContent>
                        {branches.map((branch) => (
                          <SelectItem key={branch.id} value={branch.id}>
                            {branch.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="date_of_joining">Date of Joining</Label>
                    <Input
                      id="date_of_joining"
                      type="date"
                      value={formData.date_of_joining}
                      onChange={(e) => setFormData({ ...formData, date_of_joining: e.target.value })}
                      data-testid="staff-doj-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="date_of_birth">Date of Birth</Label>
                    <Input
                      id="date_of_birth"
                      type="date"
                      value={formData.date_of_birth}
                      onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                      data-testid="staff-dob-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="social_caste">Social Caste</Label>
                    <Select
                      value={formData.social_caste}
                      onValueChange={(value) => setFormData({ ...formData, social_caste: value })}
                    >
                      <SelectTrigger data-testid="staff-caste-select">
                        <SelectValue placeholder="Select caste" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="SC">SC</SelectItem>
                        <SelectItem value="ST">ST</SelectItem>
                        <SelectItem value="OBC">OBC</SelectItem>
                        <SelectItem value="General">General</SelectItem>
                        <SelectItem value="Minority">Minority</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="job_type">Job Type</Label>
                    <Select
                      value={formData.job_type}
                      onValueChange={(value) => setFormData({ ...formData, job_type: value })}
                    >
                      <SelectTrigger data-testid="staff-job-type-select">
                        <SelectValue placeholder="Select job type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Permanent">Permanent</SelectItem>
                        <SelectItem value="Contractual">Contractual</SelectItem>
                        <SelectItem value="Part Time">Part Time</SelectItem>
                        <SelectItem value="Consultant">Consultant</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Admin-only Status Control */}
                {isAdmin() && editMode && (
                  <div className="space-y-2 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <Label className="text-sm font-semibold text-blue-900">Staff Status (Admin Only)</Label>
                    <Select
                      value={formData.status || 'active'}
                      onValueChange={(value) => setFormData({ ...formData, status: value })}
                    >
                      <SelectTrigger data-testid="staff-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="mobile">Mobile Number *</Label>
                    <Input
                      id="mobile"
                      value={formData.mobile}
                      onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                      required
                      data-testid="staff-mobile-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="gender">Gender *</Label>
                    <Select
                      value={formData.gender}
                      onValueChange={(value) => setFormData({ ...formData, gender: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Female">Female</SelectItem>
                        <SelectItem value="Male">Male</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address">Address *</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    required
                    data-testid="staff-address-input"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="state">State</Label>
                    <Select
                      value={formData.state}
                      onValueChange={(value) => setFormData({ ...formData, state: value, district: '' })}
                    >
                      <SelectTrigger data-testid="staff-state-select">
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        {getStates().map((state) => (
                          <SelectItem key={state} value={state}>{state}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="district">District</Label>
                    <Select
                      value={formData.district}
                      onValueChange={(value) => setFormData({ ...formData, district: value })}
                      disabled={!formData.state}
                    >
                      <SelectTrigger data-testid="staff-district-select">
                        <SelectValue placeholder={formData.state ? "Select district" : "Select state first"} />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        {getDistricts(formData.state).map((district) => (
                          <SelectItem key={district} value={district}>{district}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* ID Document Section */}
                <div className="space-y-2 p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <Label className="text-sm font-semibold text-amber-900">ID Document Details</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                    <div className="space-y-2">
                      <Label htmlFor="id_type">ID Type</Label>
                      <Select
                        value={formData.id_type}
                        onValueChange={(value) => setFormData({ ...formData, id_type: value })}
                      >
                        <SelectTrigger data-testid="staff-id-type-select">
                          <SelectValue placeholder="Select ID type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Aadhaar">Aadhaar</SelectItem>
                          <SelectItem value="Voter ID">Voter ID</SelectItem>
                          <SelectItem value="Driving License">Driving License</SelectItem>
                          <SelectItem value="PAN">PAN</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="id_number">ID Number</Label>
                      <Input
                        id="id_number"
                        value={formData.id_number}
                        onChange={(e) => setFormData({ ...formData, id_number: e.target.value })}
                        placeholder={formData.id_type === 'Aadhaar' ? 'XXXX XXXX XXXX' : formData.id_type === 'PAN' ? 'ABCDE1234F' : 'Enter ID number'}
                        data-testid="staff-id-number-input"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-3">
                  <Button type="button" variant="outline" onClick={closeDialog}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    data-testid="submit-staff-button"
                    className="bg-emerald-700 hover:bg-emerald-800 text-white"
                  >
                    {editMode ? 'Update Staff' : 'Add Staff'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-wrap gap-3">
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-56" data-testid="filter-staff-type">
            <SelectValue placeholder="Filter by type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Staff</SelectItem>
            <SelectItem value="Director">Director</SelectItem>
            <SelectItem value="Mentor">Mentor</SelectItem>
            <SelectItem value="Program Coordinator">Program Coordinator</SelectItem>
            <SelectItem value="Project Manager">Project Manager</SelectItem>
            <SelectItem value="Asst. Mentor">Asst. Mentor</SelectItem>
            <SelectItem value="MTS">MTS</SelectItem>
            <SelectItem value="Field Officer">Field Officer</SelectItem>
            <SelectItem value="CRP-EP">CRP-EP</SelectItem>
            <SelectItem value="Accountant">Accountant</SelectItem>
            <SelectItem value="State Coordinator">State Coordinator</SelectItem>
          </SelectContent>
        </Select>
        {isAdmin() && (
          <Select value={selectedBranchFilter} onValueChange={setSelectedBranchFilter}>
            <SelectTrigger className="w-52" data-testid="branch-filter-staff">
              <SelectValue placeholder="All Branches" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Branches</SelectItem>
              {branches.map(b => (
                <SelectItem key={b.id} value={b.id}>{b.code} - {b.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Button
          variant="outline"
          onClick={() => printStaffList(staff, branches, selectedBranchFilter, filterType)}
          data-testid="print-staff-button"
          className="border-slate-300"
        >
          <Printer className="w-4 h-4 mr-2" />
          Print
        </Button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-700"></div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex-1 min-h-[calc(100vh-280px)]">
          <div className="overflow-auto h-full max-h-[calc(100vh-280px)]">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Photo
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Branch
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Mobile
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Gender
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {staff.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-slate-500">
                      No staff found
                    </td>
                  </tr>
                ) : (
                  staff.map((member) => {
                    const branch = branches.find(b => b.id === member.branch_id);
                    return (
                    <tr key={member.id} className="hover:bg-slate-50 transition-colors" data-testid="staff-row">
                      <td className="px-4 py-3">
                        <StaffAvatar member={member} />
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => navigate(`/staff/${member.id}`)}
                          className="text-sm font-medium text-emerald-700 hover:text-emerald-800 hover:underline text-left"
                          data-testid="staff-name-link"
                        >
                          {member.name}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs px-2.5 py-0.5 rounded-full font-medium bg-blue-100 text-blue-800 border border-blue-200">
                          {member.staff_type}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs px-2.5 py-0.5 rounded-full font-medium bg-purple-100 text-purple-800 border border-purple-200">
                          {branch?.name || 'Unknown'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">{member.mobile}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{member.gender}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${
                            member.status === 'active'
                              ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                              : 'bg-slate-100 text-slate-700 border border-slate-200'
                          }`}
                        >
                          {member.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => navigate(`/staff/${member.id}`)}
                            data-testid="view-staff-button"
                            className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEdit(member)}
                            data-testid="edit-staff-button"
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          {isAdmin() && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openDeleteDialog(member)}
                              data-testid="delete-staff-button"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );})
                )}
              </tbody>
            </table>
          </div>
          <PaginationControls
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setPage}
            onPageSizeChange={(n) => { setPageSize(n); setPage(0); }}
            label="staff members"
          />
        </div>
      )}
      
      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={() => itemToDelete && handleDelete(itemToDelete.id)}
        title="Delete Staff Member"
        description="Are you sure you want to delete this staff member? This action cannot be undone."
        itemName={itemToDelete?.name}
      />
    </div>
  );
};

export default Staff;
